import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Database,
  ShieldAlert,
  Trash2,
  Receipt,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

// Phase R1 — Operational Data Quality audit page.
// Strictly read-only. Shows the operator how much of the catalogue
// is still running on synthetic Pydantic defaults vs real values.
// See /app/memory/OPERATIONAL_TRANSITION_PLAN.md §2.

const PARAM_LABELS = {
  reorder_point: 'Ponto de reposição',
  optimal_stock: 'Stock óptimo',
  safety_stock: 'Stock de segurança',
  moq: 'MOQ',
  lead_time_days: 'Lead time (dias)',
};

const QUALITY_META = {
  synthetic: {
    label: 'Sintético',
    description: 'Todos os parâmetros iguais ao default Pydantic — não fiável para sugestões.',
    color: 'bg-slate-100 text-slate-700 border-slate-300',
  },
  partial: {
    label: 'Parcial',
    description: 'Pelo menos 1 parâmetro distinto do default; ainda incompleto.',
    color: 'bg-amber-50 text-amber-800 border-amber-300',
  },
  real: {
    label: 'Real',
    description: '≥ 3 parâmetros distintos dos defaults — apto para sugestões accionáveis.',
    color: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  },
};

const Pct = ({ value }) => (
  <span className="font-mono tabular-nums">{value?.toFixed?.(2) ?? value}%</span>
);

const Num = ({ value }) => (
  <span className="font-mono tabular-nums">{value?.toLocaleString?.('pt-PT') ?? value}</span>
);

const Bar = ({ pct, tone = 'slate' }) => {
  const toneMap = {
    slate: 'bg-slate-400',
    amber: 'bg-amber-400',
    emerald: 'bg-emerald-500',
    red: 'bg-red-400',
  };
  return (
    <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
      <div
        className={`h-full ${toneMap[tone] || toneMap.slate} transition-all`}
        style={{ width: `${Math.min(100, pct)}%` }}
      />
    </div>
  );
};

const DataQuality = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pvp1, setPvp1] = useState(null);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupApplying, setCleanupApplying] = useState(false);
  const [purity, setPurity] = useState(null);
  const [purityBuckets, setPurityBuckets] = useState({ mock_demo: true, manually_created: false, orphan_unclassified: true });
  const [purityPurging, setPurityPurging] = useState(false);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const [dqRes, pvp1Res, cleanupRes, purityRes] = await Promise.all([
        axios.get(`${API}/admin/data-quality`),
        axios.get(`${API}/admin/pvp1-coverage`),
        axios.get(`${API}/admin/cleanup-synthetic-defaults/preview`),
        axios.get(`${API}/admin/data-purity/audit`),
      ]);
      setData(dqRes.data);
      setPvp1(pvp1Res.data);
      setCleanupPreview(cleanupRes.data);
      setPurity(purityRes.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Falha ao carregar audit de data quality';
      toast.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const applyCleanup = async () => {
    if (!cleanupPreview || cleanupPreview.products_with_any_synthetic_field === 0) return;
    const n = cleanupPreview.products_with_any_synthetic_field;
    if (!window.confirm(
      `Vai limpar valores sintéticos (10/100/5/1/7) de ${n} produtos.\n\n` +
      `O comando faz $unset apenas onde o valor coincide exactamente com o default.\n` +
      `Valores reais ou manuais são preservados.\n\n` +
      `Confirmar?`,
    )) return;
    setCleanupApplying(true);
    try {
      const res = await axios.post(`${API}/admin/cleanup-synthetic-defaults/apply`, {
        confirm: true,
      });
      toast.success(`Limpeza concluída: ${res.data.total_modifications} campos removidos`);
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha na limpeza');
    } finally {
      setCleanupApplying(false);
    }
  };

  const purgeOrphans = async () => {
    if (!purity) return;
    const buckets = Object.entries(purityBuckets)
      .filter(([, on]) => on)
      .map(([b]) => b);
    if (buckets.length === 0) {
      toast.error('Selecciona pelo menos 1 bucket para remover');
      return;
    }
    const total = buckets.reduce((acc, b) => acc + (purity.buckets[b] || 0), 0);
    if (total === 0) {
      toast.error('Os buckets seleccionados estão vazios');
      return;
    }
    const dep = purity.dependents_for_non_synced || {};
    const confirmMsg =
      `Vai remover ${total} produtos das categorias: ${buckets.join(', ')}.\n\n` +
      `Cascade afecta dependentes não-syncados:\n` +
      `  sales_history: ${dep.sales_history || 0}\n` +
      `  stock_alerts: ${dep.stock_alerts || 0}\n` +
      `  purchase_orders: ${dep.purchase_orders || 0}\n` +
      `  stock_movements: ${dep.stock_movements || 0}\n` +
      `  product_supplier_procurement: ${dep.product_supplier_procurement || 0}\n` +
      `  product_suppliers: ${dep.product_suppliers || 0}\n` +
      `  consumption_records (items): ${dep.consumption_records_items || 0}\n\n` +
      `Produtos sincronizados do SAGE NÃO são afectados.\n\n` +
      `Confirmar?`;
    if (!window.confirm(confirmMsg)) return;
    setPurityPurging(true);
    try {
      const res = await axios.post(`${API}/admin/data-purity/purge-orphans`, {
        confirm: true,
        buckets,
      });
      toast.success(`${res.data.products_removed} produtos removidos`);
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha na limpeza');
    } finally {
      setPurityPurging(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center min-h-[60vh]"
        data-testid="data-quality-loading"
      >
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-700" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8 text-slate-600" data-testid="data-quality-empty">
        Sem dados disponíveis.
      </div>
    );
  }

  const total = data.totals.total_products || 1;
  const dist = data.config_quality_distribution;
  const pcts = data.percentages;
  const verdict = data.r2_readiness;
  const verdictOk = verdict.verdict === 'GO';

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="data-quality-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Database className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
                Data Quality — Phase R1
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Análise de qualidade de dados — catálogo real vs defaults sintéticos
              </p>
            </div>
            <Button
              variant="outline"
              onClick={fetchData}
              disabled={refreshing}
              data-testid="data-quality-refresh-btn"
              className="shrink-0"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Recalcular
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6 space-y-6">
        {/* Procurement scope */}
      {data.procurement_scope && (
        <Card className="border-2 border-slate-200" data-testid="procurement-scope-banner">
          <CardContent className="p-5">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-base font-semibold text-slate-800">
                  Scope actual do cockpit (opt-in)
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Apenas SKUs com AI tracking activo + supplier activado entram no
                  cockpit (alertas, sugestões, forecasting).
                </p>
              </div>
              <div className="flex gap-6 text-sm">
                <div className="text-center">
                  <p className="text-xs uppercase text-slate-500">SKUs no cockpit</p>
                  <p className="text-2xl font-semibold text-emerald-700 font-mono">
                    <Num value={data.procurement_scope.products_in_scope} />
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs uppercase text-slate-500">Fornecedores activos</p>
                  <p className="text-2xl font-semibold text-slate-800 font-mono">
                    {data.procurement_scope.suppliers_pi_enabled}/{data.procurement_scope.suppliers_total}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Verdict banner */}
      <Card
        className={`border-2 ${verdictOk ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}
        data-testid="data-quality-verdict"
      >
        <CardContent className="p-5 flex items-start gap-4">
          {verdictOk ? (
            <CheckCircle2 className="h-6 w-6 text-emerald-600 mt-0.5 shrink-0" />
          ) : (
            <ShieldAlert className="h-6 w-6 text-amber-600 mt-0.5 shrink-0" />
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-900">
                Veredito R2: {verdictOk ? 'GO' : 'NO GO'}
              </span>
              <Badge variant="outline" className="text-xs">
                limite {verdict.threshold_pct}%
              </Badge>
              <Badge variant="outline" className="text-xs">
                actual <Pct value={verdict.current_real_or_partial_pct} />
              </Badge>
            </div>
            <p className="text-sm text-slate-700 mt-1">{verdict.rationale}</p>
            <p className="text-xs text-slate-500 mt-2">
              Calculado em {new Date(data.computed_at).toLocaleString('pt-PT')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="totals-total-products">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-slate-500">Produtos no catálogo</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1">
              <Num value={data.totals.total_products} />
            </p>
          </CardContent>
        </Card>
        <Card data-testid="totals-with-sage">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-slate-500">Com sage_supplier_code</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1">
              <Num value={data.totals.products_with_sage_supplier_code} />
            </p>
            <p className="text-xs text-slate-500 mt-1">
              <Pct value={pcts.sage_supplier_code_pct} />
            </p>
          </CardContent>
        </Card>
        <Card data-testid="totals-with-stock">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-slate-500">Com stock &gt; 0</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1">
              <Num value={data.totals.products_with_stock} />
            </p>
          </CardContent>
        </Card>
        <Card data-testid="totals-recent-sync">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-slate-500">Sincronizados (7d)</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1">
              <Num value={data.totals.products_recently_synced_7d} />
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Synthetic defaults cleanup */}
      {cleanupPreview && (
        <Card
          className={`border ${cleanupPreview.products_with_any_synthetic_field > 0 ? 'border-red-300 bg-red-50/40' : 'border-emerald-200 bg-emerald-50/40'}`}
          data-testid="cleanup-synthetic-card"
        >
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-slate-600" />
              Limpeza de defaults sintéticos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cleanupPreview.products_with_any_synthetic_field === 0 ? (
              <div className="flex items-start gap-2 text-sm text-emerald-800">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Sem defaults sintéticos na DB.</p>
                  <p className="text-xs text-slate-600 mt-1">
                    O sistema opera em modo opt-in puro: ausência honesta de configuração para SKUs não calibrados.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-700">
                  <span className="font-mono font-semibold">
                    {cleanupPreview.products_with_any_synthetic_field.toLocaleString('pt-PT')}
                  </span>{' '}
                  produtos ainda têm pelo menos 1 campo igual ao default sintético (10/100/5/1/7).
                </p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3 text-xs">
                  {Object.entries(cleanupPreview.per_field_match_count).map(([f, n]) => (
                    <div key={f}>
                      <p className="text-slate-500 uppercase text-[10px]">{f}</p>
                      <p className="font-mono text-slate-800">
                        {n.toLocaleString('pt-PT')} = {cleanupPreview.synthetic_defaults[f]}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Estratégia: <code className="bg-slate-100 px-1 rounded">$unset</code> apenas quando o valor coincide exactamente com o default. Valores reais ou manuais são preservados.
                </p>
                <div className="flex justify-end mt-3">
                  <Button
                    variant="outline"
                    onClick={applyCleanup}
                    disabled={cleanupApplying}
                    data-testid="cleanup-apply-btn"
                    className="text-red-700 border-red-300 hover:bg-red-100"
                  >
                    {cleanupApplying && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                    Limpar defaults sintéticos
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Data purity audit — SAGE vs demo/mock */}
      {purity && (
        <Card
          className={`border ${(purity.buckets.mock_demo + purity.buckets.manually_created + purity.buckets.orphan_unclassified) > 0 ? 'border-amber-300 bg-amber-50/40' : 'border-emerald-200 bg-emerald-50/40'}`}
          data-testid="data-purity-card"
        >
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-4 w-4 text-slate-600" />
              Pureza operacional — SAGE como source-of-truth
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Produtos classificados por origem. Apenas SAGE-syncados pertencem à
              realidade operacional. Demais são candidatos a remoção.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: 'synced_from_sage', label: 'SAGE sync', color: 'bg-emerald-50 text-emerald-800 border-emerald-300', purgeable: false },
                { key: 'manually_created', label: 'Criados manualmente', color: 'bg-amber-50 text-amber-800 border-amber-300', purgeable: true },
                { key: 'mock_demo', label: 'Demo / mock', color: 'bg-red-50 text-red-800 border-red-300', purgeable: true },
                { key: 'orphan_unclassified', label: 'Orphan', color: 'bg-slate-100 text-slate-700 border-slate-300', purgeable: true },
              ].map(({ key, label, color, purgeable }) => (
                <div
                  key={key}
                  className={`rounded border p-3 ${color}`}
                  data-testid={`purity-bucket-${key}`}
                >
                  <p className="text-xs uppercase">{label}</p>
                  <p className="text-2xl font-semibold font-mono mt-1">
                    {purity.buckets[key].toLocaleString('pt-PT')}
                  </p>
                  <p className="text-xs mt-0.5">{purity.percentages[key]}%</p>
                  {purgeable && purity.buckets[key] > 0 && (
                    <label className="flex items-center gap-1.5 mt-2 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!purityBuckets[key]}
                        onChange={(e) =>
                          setPurityBuckets({ ...purityBuckets, [key]: e.target.checked })
                        }
                        data-testid={`purity-bucket-checkbox-${key}`}
                      />
                      <span>incluir na remoção</span>
                    </label>
                  )}
                </div>
              ))}
            </div>

            {/* Dependents impacted */}
            {purity.non_synced_ids_count > 0 && (
              <details className="mt-4 text-xs text-slate-600">
                <summary className="cursor-pointer hover:text-slate-900">
                  Dependentes que seriam removidos em cascade
                </summary>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2 font-mono">
                  {Object.entries(purity.dependents_for_non_synced).map(([k, v]) => (
                    <div key={k}>
                      <p className="text-slate-500 text-[10px] uppercase">{k}</p>
                      <p>{v?.toLocaleString?.('pt-PT')}</p>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Samples per bucket */}
            <details className="mt-3 text-xs text-slate-600">
              <summary className="cursor-pointer hover:text-slate-900">
                Ver amostras (15 por bucket)
              </summary>
              <div className="mt-2 space-y-3">
                {['mock_demo', 'manually_created', 'orphan_unclassified'].map((b) => (
                  purity.samples[b]?.length > 0 && (
                    <div key={b}>
                      <p className="font-semibold text-slate-700 mb-1">{b}</p>
                      <div className="space-y-0.5 pl-2 border-l border-slate-200">
                        {purity.samples[b].map((s) => (
                          <div key={s.sku} className="flex justify-between gap-2 font-mono">
                            <span className="truncate">
                              {s.sku} — <span className="text-slate-500">{s.name}</span>
                            </span>
                            <span className="text-slate-400 whitespace-nowrap">
                              {s.supplier || '—'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                ))}
              </div>
            </details>

            {(purity.buckets.mock_demo + purity.buckets.manually_created + purity.buckets.orphan_unclassified) > 0 ? (
              <div className="flex justify-end mt-4">
                <Button
                  variant="outline"
                  onClick={purgeOrphans}
                  disabled={purityPurging}
                  data-testid="purity-purge-btn"
                  className="text-red-700 border-red-300 hover:bg-red-100"
                >
                  {purityPurging && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                  Remover seleccionados (com cascade)
                </Button>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-sm text-emerald-800 mt-3">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                <p className="font-medium">Tudo limpo. Apenas SAGE-syncados na DB.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* PVP1 c/IVA coverage */}
      {pvp1 && (
        <Card className="border border-slate-200" data-testid="pvp1-coverage-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="h-4 w-4 text-slate-600" />
              PVP1 c/IVA — cobertura
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Usado pela valorização de Consumos Técnicos e Faturação. Sem fallback artificial: quando vazio fica vazio.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs uppercase text-slate-500">Sincronizados</p>
                <p className="text-xl font-semibold font-mono mt-1">
                  {pvp1.synced_total.toLocaleString('pt-PT')}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">Com PVP1 c/IVA &gt; 0</p>
                <p className="text-xl font-semibold font-mono mt-1 text-emerald-700">
                  {pvp1.synced_with_pvp1.toLocaleString('pt-PT')}
                </p>
                <p className="text-xs text-slate-500">
                  <Pct value={pvp1.pvp1_synced_pct} />
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">Com sale_price &gt; 0</p>
                <p className="text-xl font-semibold font-mono mt-1">
                  {pvp1.has_sale_price_global.toLocaleString('pt-PT')}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">Com cost_price &gt; 0</p>
                <p className="text-xl font-semibold font-mono mt-1">
                  {pvp1.has_cost_price_global.toLocaleString('pt-PT')}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <Bar pct={pvp1.pvp1_synced_pct} tone={pvp1.pvp1_synced_pct >= 90 ? 'emerald' : pvp1.pvp1_synced_pct >= 70 ? 'amber' : 'red'} />
            </div>
            {pvp1.missing_samples && pvp1.missing_samples.length > 0 && (
              <details className="mt-3 text-xs text-slate-600">
                <summary className="cursor-pointer hover:text-slate-900">
                  Ver {pvp1.missing_samples.length} amostras sem PVP1 c/IVA
                </summary>
                <div className="mt-2 space-y-1 font-mono">
                  {pvp1.missing_samples.map((s) => (
                    <div key={s.sku} className="flex justify-between border-b border-slate-100 py-1">
                      <span>{s.sku}</span>
                      <span className="text-slate-400">{s.last_synced?.slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quality distribution */}
      <Card data-testid="quality-distribution">
        <CardHeader>
          <CardTitle className="text-base">Distribuição por qualidade de configuração</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(['real', 'partial', 'synthetic']).map((q) => {
            const meta = QUALITY_META[q];
            const count = dist[q];
            const pct = (count / total) * 100;
            const tone = q === 'real' ? 'emerald' : q === 'partial' ? 'amber' : 'slate';
            return (
              <div key={q} data-testid={`quality-row-${q}`}>
                <div className="flex justify-between items-end mb-1">
                  <div>
                    <Badge variant="outline" className={meta.color}>
                      {meta.label}
                    </Badge>
                    <span className="text-xs text-slate-500 ml-2">{meta.description}</span>
                  </div>
                  <div className="text-sm text-slate-700">
                    <Num value={count} /> SKUs · <Pct value={pct} />
                  </div>
                </div>
                <Bar pct={pct} tone={tone} />
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Per-field defaults */}
      <Card data-testid="per-field-defaults">
        <CardHeader>
          <CardTitle className="text-base">
            Parâmetros ainda no default Pydantic
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Parâmetro</TableHead>
                <TableHead className="text-right">Default sintético</TableHead>
                <TableHead className="text-right">SKUs no default</TableHead>
                <TableHead className="text-right">% catálogo</TableHead>
                <TableHead className="w-1/4">Distribuição</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(data.synthetic_defaults).map(([field, defaultValue]) => {
                const count = data.default_value_counts[field] || 0;
                const pct = (count / total) * 100;
                const tone = pct > 90 ? 'red' : pct > 50 ? 'amber' : 'emerald';
                return (
                  <TableRow key={field} data-testid={`field-row-${field}`}>
                    <TableCell className="font-medium text-slate-800">
                      {PARAM_LABELS[field] || field}
                    </TableCell>
                    <TableCell className="text-right font-mono text-slate-500">
                      {defaultValue}
                    </TableCell>
                    <TableCell className="text-right">
                      <Num value={count} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Pct value={pct} />
                    </TableCell>
                    <TableCell>
                      <Bar pct={pct} tone={tone} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <p className="text-xs text-slate-500 mt-3 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-500" />
            Estas células altas em vermelho indicam parâmetros que nunca foram calibrados.
            R3 (calibração com COO) tem como objectivo reduzir &gt;90% para &lt;30%.
          </p>
        </CardContent>
      </Card>

      {/* Samples */}
      <Card data-testid="samples-card">
        <CardHeader>
          <CardTitle className="text-base">Amostras por bucket</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="real">
            <TabsList>
              <TabsTrigger value="real" data-testid="samples-tab-real">
                Real ({dist.real})
              </TabsTrigger>
              <TabsTrigger value="partial" data-testid="samples-tab-partial">
                Parcial ({dist.partial})
              </TabsTrigger>
              <TabsTrigger value="synthetic" data-testid="samples-tab-synthetic">
                Sintético ({dist.synthetic})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="real">
              {data.samples.real.length === 0 ? (
                <p className="text-sm text-slate-500 p-4">Nenhum SKU classificado como real.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead className="text-right">RP</TableHead>
                      <TableHead className="text-right">Óptimo</TableHead>
                      <TableHead className="text-right">SS</TableHead>
                      <TableHead className="text-right">MOQ</TableHead>
                      <TableHead className="text-right">LT</TableHead>
                      <TableHead className="text-right">Distintos</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.samples.real.map((p) => (
                      <TableRow key={p.sku} data-testid={`sample-real-${p.sku}`}>
                        <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                        <TableCell className="text-slate-700 max-w-xs truncate">{p.name}</TableCell>
                        <TableCell className="text-right font-mono">{p.reorder_point ?? '—'}</TableCell>
                        <TableCell className="text-right font-mono">{p.optimal_stock ?? '—'}</TableCell>
                        <TableCell className="text-right font-mono">{p.safety_stock ?? '—'}</TableCell>
                        <TableCell className="text-right font-mono">{p.moq ?? '—'}</TableCell>
                        <TableCell className="text-right font-mono">{p.lead_time_days ?? '—'}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                            {p.differ_count}/5
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="partial">
              {data.samples.partial.length === 0 ? (
                <p className="text-sm text-slate-500 p-4">Nenhum SKU classificado como parcial.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Sage code</TableHead>
                      <TableHead className="text-right">Distintos</TableHead>
                      <TableHead>Em default</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.samples.partial.map((p) => (
                      <TableRow key={p.sku} data-testid={`sample-partial-${p.sku}`}>
                        <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                        <TableCell className="text-slate-700 max-w-xs truncate">{p.name}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">
                          {p.sage_supplier_code || '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline" className="bg-amber-50 text-amber-700">
                            {p.differ_count}/5
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {Object.entries(p.fields_at_default || {})
                            .filter(([, v]) => v)
                            .map(([k]) => PARAM_LABELS[k] || k)
                            .join(', ')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="synthetic">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Fornecedor</TableHead>
                    <TableHead>Sage code</TableHead>
                    <TableHead className="text-right">Stock</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.samples.synthetic.map((p) => (
                    <TableRow key={p.sku} data-testid={`sample-synthetic-${p.sku}`}>
                      <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                      <TableCell className="text-slate-700 max-w-xs truncate">{p.name}</TableCell>
                      <TableCell className="text-slate-600 max-w-xs truncate">{p.supplier || '—'}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">
                        {p.sage_supplier_code || '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono">{p.current_stock ?? 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default DataQuality;
